import { useEffect, useState } from 'react';
import api from '../../api/client';

/**
 * Tela cheia de bloqueio comercial (PLANO_UNIFICADO §6.4).
 *
 * Acionada por 2 caminhos:
 *  1) Axios interceptor disparou evento `access:blocked` ao receber 402.
 *  2) Mount inicial faz GET /api/access/status e checa Status=Blocked.
 *
 * Quando bloqueada, sobrepõe todo o app — impede uso até regularizar pagamento.
 * Botão "Verificar pagamento" força refresh imediato no Hub via POST /api/access/refresh.
 */

interface BlockedDetail {
  message?: string;
  reason?: string;
  daysPastDue?: number;
}

interface AccessStatusResponse {
  status: 'Allowed' | 'Warning' | 'Blocked' | 'Unknown';
  reason: string;
  message: string;
  daysPastDue?: number | null;
}

export default function AccessGate() {
  const [blocked, setBlocked] = useState(false);
  const [detail, setDetail] = useState<BlockedDetail>({});
  const [checking, setChecking] = useState(false);
  const [warning, setWarning] = useState<{ message: string; daysPastDue?: number | null } | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await api.get<AccessStatusResponse>('/access/status');
        const data = res.data;
        if (data.status === 'Blocked') {
          setBlocked(true);
          setDetail({ message: data.message, reason: data.reason, daysPastDue: data.daysPastDue ?? undefined });
        } else if (data.status === 'Warning') {
          setWarning({ message: data.message, daysPastDue: data.daysPastDue });
        }
      } catch {
        // fail-open: se o endpoint não responde, libera UI
      }
    };
    checkStatus();

    const handler = (e: Event) => {
      const ev = e as CustomEvent<BlockedDetail>;
      setBlocked(true);
      setDetail(ev.detail ?? {});
    };
    window.addEventListener('access:blocked', handler);
    return () => window.removeEventListener('access:blocked', handler);
  }, []);

  const recheck = async () => {
    setChecking(true);
    try {
      const res = await api.post<AccessStatusResponse>('/access/refresh');
      if (res.data.status !== 'Blocked') {
        setBlocked(false);
        setDetail({});
        if (res.data.status === 'Warning') {
          setWarning({ message: res.data.message, daysPastDue: res.data.daysPastDue });
        }
      } else {
        setDetail({ message: res.data.message, reason: res.data.reason, daysPastDue: res.data.daysPastDue ?? undefined });
      }
    } catch {
      // ignora
    } finally {
      setChecking(false);
    }
  };

  if (blocked) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-zinc-900/95 p-6 backdrop-blur">
        <div className="w-full max-w-lg rounded-xl bg-white p-8 shadow-2xl dark:bg-zinc-800">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7"><path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" /></svg>
            </div>
            <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Acesso suspenso</h1>
          </div>
          <p className="mb-2 text-sm text-zinc-700 dark:text-zinc-300">
            {detail.message ?? 'Acesso suspenso por inadimplência. Regularize o pagamento pra reativar.'}
          </p>
          {detail.daysPastDue != null && detail.daysPastDue > 0 && (
            <p className="mb-4 text-sm font-semibold text-rose-600 dark:text-rose-400">
              {detail.daysPastDue} dia{detail.daysPastDue === 1 ? '' : 's'} em atraso.
            </p>
          )}
          <div className="mb-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
            Após o pagamento, o desbloqueio é automático em alguns minutos. Se já pagou, clique abaixo pra verificar.
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              onClick={recheck}
              disabled={checking}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:opacity-60"
            >
              {checking ? 'Verificando…' : 'Verificar pagamento'}
            </button>
          </div>
          {detail.reason && (
            <p className="mt-4 text-[10px] uppercase tracking-wide text-zinc-400">Motivo: {detail.reason}</p>
          )}
        </div>
      </div>
    );
  }

  if (warning) {
    return (
      <div className="sticky top-0 z-[9998] border-b border-amber-300 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        ⚠ {warning.message || `Cobrança em atraso${warning.daysPastDue ? ` (${warning.daysPastDue}d)` : ''}. Regularize antes do bloqueio.`}
      </div>
    );
  }

  return null;
}
